import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext, API } from '@/App';
import AdminLayout from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Plus, Edit2, Trash2, Loader2, 
  Check, X, RefreshCw, Package, Eye, EyeOff
} from 'lucide-react';

const VEHICLE_TYPES = {
  small: { label: 'Carro Pequeno', icon: '🚗', color: 'bg-blue-100 text-blue-700' },
  suv: { label: 'SUV', icon: '🚙', color: 'bg-green-100 text-green-700' },
  pickup: { label: 'Caminhonete', icon: '🛻', color: 'bg-orange-100 text-orange-700' }
};

export default function AdminPlans() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    vehicle_type: 'small',
    monthly_price: '',
    description: '',
    services: [
      { type: 'complete', quantity: 1, price_per_unit: 35 },
      { type: 'ducha', quantity: 2, price_per_unit: 15 }
    ]
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user || user.user_type !== 'admin') {
      navigate('/control-portal-clube/login');
      return;
    }
    fetchPlans();
  }, [user, navigate, showInactive]);

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/plans?show_inactive=${showInactive}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPlans(res.data.plans || []);
    } catch (error) {
      console.error(error);
      toast.error('Erro ao carregar planos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...formData,
        monthly_price: parseFloat(formData.monthly_price),
        services: formData.services.filter(s => s.quantity > 0).map(s => ({
          ...s,
          price_per_unit: parseFloat(s.price_per_unit)
        }))
      };

      if (editingPlan) {
        await axios.put(`${API}/admin/plans/${editingPlan.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Plano atualizado!');
      } else {
        await axios.post(`${API}/admin/plans`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Plano criado!');
      }
      
      setShowForm(false);
      setEditingPlan(null);
      resetForm();
      fetchPlans();
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || 'Erro ao salvar plano');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (plan) => {
    setEditingPlan(plan);
    setFormData({
      name: plan.name,
      vehicle_type: plan.vehicle_type,
      monthly_price: plan.monthly_price.toString(),
      description: plan.description || '',
      services: plan.services || []
    });
    setShowForm(true);
  };

  const handleToggle = async (planId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/plans/${planId}/toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Status alterado!');
      fetchPlans();
    } catch (error) {
      toast.error('Erro ao alterar status');
    }
  };

  const handleDelete = async (planId) => {
    if (!window.confirm('Tem certeza que deseja remover este plano?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/plans/${planId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Plano removido!');
      fetchPlans();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao remover plano');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      vehicle_type: 'small',
      monthly_price: '',
      description: '',
      services: [
        { type: 'complete', quantity: 1, price_per_unit: 35 },
        { type: 'ducha', quantity: 2, price_per_unit: 15 }
      ]
    });
  };

  const updateService = (index, field, value) => {
    const updated = [...formData.services];
    updated[index][field] = field === 'type' ? value : parseFloat(value) || 0;
    setFormData({ ...formData, services: updated });
  };

  if (loading) {
    return (
      <AdminLayout title="Gestão de Planos" subtitle="Carregando...">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Gestão de Planos</h1>
            <p className="text-slate-500">{plans.length} planos cadastrados</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={fetchPlans}>
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Button onClick={() => { resetForm(); setEditingPlan(null); setShowForm(true); }}>
              <Plus className="w-4 h-4 mr-2" />
              Novo Plano
            </Button>
          </div>
          
          {/* Filtro de planos inativos */}
          <div className="flex items-center gap-2 text-sm">
            <Checkbox 
              id="show-inactive"
              checked={showInactive}
              onCheckedChange={setShowInactive}
            />
            <label 
              htmlFor="show-inactive" 
              className="cursor-pointer text-muted-foreground flex items-center gap-1"
            >
              {showInactive ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              Mostrar planos inativos
            </label>
          </div>
        </div>

        {/* Form Modal */}
        {showForm && (
          <Card className="p-6 mb-6">
            <h2 className="text-lg font-semibold mb-4">
              {editingPlan ? 'Editar Plano' : 'Novo Plano'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Nome do Plano</Label>
                  <Input 
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Ex: Carro Pequeno - Premium"
                    required
                  />
                </div>
                <div>
                  <Label>Tipo de Veículo</Label>
                  <select 
                    className="w-full h-10 px-3 border rounded-md"
                    value={formData.vehicle_type}
                    onChange={(e) => setFormData({ ...formData, vehicle_type: e.target.value })}
                  >
                    {Object.entries(VEHICLE_TYPES).map(([key, val]) => (
                      <option key={key} value={key}>{val.icon} {val.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>Preço Mensal (R$)</Label>
                  <Input 
                    type="number"
                    step="0.01"
                    value={formData.monthly_price}
                    onChange={(e) => setFormData({ ...formData, monthly_price: e.target.value })}
                    placeholder="99.00"
                    required
                  />
                </div>
                <div>
                  <Label>Descrição</Label>
                  <Input 
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Ex: 4 Lavagens completas"
                  />
                </div>
              </div>

              {/* Serviços incluídos */}
              <div>
                <Label className="mb-2 block">Serviços Incluídos</Label>
                <div className="space-y-2">
                  {formData.services.map((service, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-2 bg-slate-50 rounded">
                      <select 
                        className="h-9 px-2 border rounded text-sm"
                        value={service.type}
                        onChange={(e) => updateService(idx, 'type', e.target.value)}
                      >
                        <option value="complete">Lavagem Completa</option>
                        <option value="ducha">Ducha Simples</option>
                      </select>
                      <div className="flex items-center gap-1">
                        <span className="text-sm text-muted-foreground">Qtd:</span>
                        <Input 
                          type="number"
                          className="w-16 h-9"
                          value={service.quantity}
                          onChange={(e) => updateService(idx, 'quantity', e.target.value)}
                          min="0"
                        />
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-sm text-muted-foreground">R$/un:</span>
                        <Input 
                          type="number"
                          step="0.01"
                          className="w-20 h-9"
                          value={service.price_per_unit}
                          onChange={(e) => updateService(idx, 'price_per_unit', e.target.value)}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => { setShowForm(false); setEditingPlan(null); }}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={saving}>
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {editingPlan ? 'Salvar Alterações' : 'Criar Plano'}
                </Button>
              </div>
            </form>
          </Card>
        )}

        {/* Lista de Planos por Tipo de Veículo */}
        {Object.entries(VEHICLE_TYPES).map(([vehicleType, config]) => {
          const typePlans = plans.filter(p => p.vehicle_type === vehicleType);
          if (typePlans.length === 0) return null;
          
          return (
            <div key={vehicleType} className="mb-6">
              <h3 className="text-md font-semibold mb-3 flex items-center gap-2">
                <span>{config.icon}</span>
                {config.label}
                <Badge variant="outline">{typePlans.length} planos</Badge>
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {typePlans.map(plan => {
                  // Compatibilidade: verificar is_active OU status
                  const isActive = plan.is_active === true || plan.status === 'ACTIVE';
                  
                  return (
                    <Card key={plan.id} className={`p-4 ${!isActive ? 'opacity-60 border-dashed' : ''}`}>
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h4 className="font-semibold">{plan.name}</h4>
                          <p className="text-sm text-muted-foreground">{plan.description}</p>
                        </div>
                        <Badge className={isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}>
                          {isActive ? 'Ativo' : 'Inativo'}
                        </Badge>
                      </div>
                      
                      <div className="flex items-baseline gap-1 mb-3">
                        <span className="text-2xl font-bold text-primary">R$ {plan.monthly_price?.toFixed(2) || '0.00'}</span>
                        <span className="text-sm text-muted-foreground">/mês</span>
                      </div>

                      {plan.services && plan.services.length > 0 && (
                        <div className="mb-3 space-y-1">
                          {plan.services.map((s, i) => (
                            <div key={i} className="text-sm flex justify-between">
                              <span>{s.quantity}x {s.type === 'complete' ? 'Lavagem Completa' : 'Ducha'}</span>
                              <span className="text-muted-foreground">R$ {s.price_per_unit?.toFixed(2)}/un</span>
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="flex gap-2 pt-2 border-t">
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(plan)}>
                          <Edit2 className="w-4 h-4 mr-1" /> Editar
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleToggle(plan.id)}>
                          {isActive ? <X className="w-4 h-4 mr-1" /> : <Check className="w-4 h-4 mr-1" />}
                          {isActive ? 'Desativar' : 'Ativar'}
                        </Button>
                        <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDelete(plan.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </Card>
                  );
                })}
              </div>
            </div>
          );
        })}

        {plans.length === 0 && (
          <Card className="p-8 text-center">
            <Package className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">Nenhum plano cadastrado</h3>
            <p className="text-muted-foreground mb-4">Crie seu primeiro plano de assinatura</p>
            <Button onClick={() => setShowForm(true)}>
              <Plus className="w-4 h-4 mr-2" /> Criar Plano
            </Button>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
}
